import React, { FunctionComponent, useEffect, useState } from "react"
import { useNotification } from "../../common/hooks/useNotification"
import promptStyles from "./PromptText.module.scss"
import manualPageStyles from "./ManualPage.module.scss"
import { useManualPageContext } from "./useManualPageContext"
import {
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@material-ui/core"
import {
    advanceStepper,
    Fields,
    reverseStepper,
    updateSourceLanguage,
    updateTargetLanguage,
    updateText
} from "./reducer"

export const PromptText: FunctionComponent = () => {
    const {
        checkForFileNameError,
        translateTextLoading,
        translationData,
        translationError,
        handleTranslate,
        state,
        dispatch
    } = useManualPageContext()
    const { notifyError, notifySuccess } = useNotification()
    const [nextDisabled, setNextDisabled] = useState(false)

    useEffect(() => {
        if (
            !!translationData &&
            !state.fields.audioFileName.error &&
            !state.fields.originalText.error &&
            !state.fields.translatedText.error &&
            !!state.fields.originalText.value &&
            !!state.fields.audioFileName.value
        ) {
            setNextDisabled(false)
        }
    }, [
        translationData,
        state.fields.audioFileName.error,
        state.fields.originalText.error,
        state.fields.translatedText.error,
        state.fields.originalText.value,
        state.fields.audioFileName.value
    ])

    useEffect(() => {
        setNextDisabled(
            !!state.fields.audioFileName.error ||
                !!translationError ||
                !!state.fields.originalText.error ||
                !!state.fields.translatedText.error ||
                !state.fields.originalText.value ||
                !state.fields.audioFileName.value
        )
    }, [
        translationError,
        state.fields.audioFileName.error,
        state.fields.originalText.error,
        state.fields.translatedText.error,
        state.fields.originalText.value,
        state.fields.audioFileName.value
    ])

    return (
        <Grid container alignItems="stretch" spacing={3}>
            <Grid item className={manualPageStyles.Step__description}>
                <Typography variant="caption">
                    Enter your prompt text. If you need to translate text, click translate, or your
                    may provide your own translated text.
                </Typography>
            </Grid>
            <Grid item className={manualPageStyles.Step__content}>
                <Grid
                    container
                    spacing={3}
                    className={promptStyles.PromptText}
                    direction="column"
                    justify="flex-start"
                    alignItems="stretch"
                >
                    <Grid item>
                        <Typography variant="h6" paragraph>
                            Prompt Text
                        </Typography>
                    </Grid>

                    <Grid item className={promptStyles.PromptText__griditem}>
                        <FormControl>
                            <TextField
                                required
                                error={
                                    !!state.fields.audioFileName.error ||
                                    !state.fields.audioFileName.value
                                }
                                id={Fields.AudioFileName}
                                value={state.fields.audioFileName.value}
                                label={"Prompt Name"}
                                onChange={e =>
                                    dispatch(updateText(Fields.AudioFileName, e.target.value))
                                }
                                onBlur={e => checkForFileNameError()}
                                variant="outlined"
                            />
                            <FormHelperText
                                error={!!state.fields.audioFileName.error}
                                data-testid="fileNameError"
                            >
                                {state.fields.audioFileName.error}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <FormControl variant="outlined" id="select-source-language-control">
                            <InputLabel id="select-source-language-label">
                                Source Language
                            </InputLabel>
                            <Select
                                inputProps={{ "data-testid": "sourceLanguageSelector" }}
                                labelId="select-source-language-label"
                                id="select-source-language"
                                onChange={(e: any) => {
                                    dispatch(updateSourceLanguage(e.target.value))
                                }}
                                label="Source Language"
                                value={state.sourceLanguage ? state.sourceLanguage : ""}
                            >
                                {state.languageOptions.source.map(language => (
                                    <MenuItem key={language.name} value={language.id}>
                                        {language.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl className={promptStyles.PromptText__griditem}>
                            <TextField
                                required
                                error={!!state.fields.originalText.error}
                                id={Fields.OriginalText}
                                value={state.fields.originalText.value}
                                inputProps={{ maxLength: 3000, "data-testid": "originalTextInput" }}
                                onChange={e => {
                                    dispatch(updateText(Fields.OriginalText, e.target.value))
                                }}
                                multiline
                                fullWidth={true}
                                rows={10}
                                label="Source Text"
                                variant="outlined"
                            />
                            <FormHelperText
                                error={!!state.fields.originalText.error}
                                data-testid="originalTextError"
                            >
                                {state.fields.originalText.error}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <Typography
                            variant="subtitle1"
                            className={promptStyles.OptionalHeader}
                            gutterBottom
                        >
                            (Optional)
                        </Typography>
                    </Grid>

                    <Grid item>
                        <FormControl variant="outlined" id="select-target-language-control">
                            <InputLabel id="select-target-language-label">
                                Target Language
                            </InputLabel>
                            <Select
                                inputProps={{ "data-testid": "targetLanguageSelector" }}
                                labelId="select-target-language-label"
                                id="select-target-language"
                                onChange={(e: any) => {
                                    dispatch(updateTargetLanguage(e.target.value))
                                }}
                                label="Target Language"
                                value={state.targetLanguage ? state.targetLanguage : ""}
                            >
                                {state.languageOptions.target.map(language => (
                                    <MenuItem key={language.name} value={language.id}>
                                        {language.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <FormControl className={promptStyles.PromptText__griditem}>
                            {translateTextLoading && <CircularProgress />}
                            {!translateTextLoading && (
                                <TextField
                                    id={Fields.TranslatedText}
                                    error={!!state.fields.translatedText.error}
                                    value={state.fields.translatedText.value}
                                    inputProps={{
                                        maxLength: 3500,
                                        "data-testid": "translatedTextInput"
                                    }}
                                    onChange={e => {
                                        dispatch(updateText(Fields.TranslatedText, e.target.value))
                                    }}
                                    multiline
                                    fullWidth={true}
                                    rows={10}
                                    label={"Target Text"}
                                    variant="outlined"
                                />
                            )}
                            <FormHelperText
                                error={!!state.fields.translatedText.error}
                                data-testid="translatedTextError"
                            >
                                {state.fields.translatedText.error}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <Button
                            id="translate"
                            name="translate"
                            type="submit"
                            value="Translate"
                            disabled={translateTextLoading}
                            onClick={() => {
                                handleTranslate()
                                setNextDisabled(true)
                            }}
                            variant="contained"
                        >
                            Translate
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item className={manualPageStyles.Step__content}>
                <Button
                    data-testid="promptTextBack"
                    id="promptTextBack"
                    disabled={true}
                    onClick={() => {
                        dispatch(reverseStepper())
                    }}
                >
                    Back
                </Button>
                <Button
                    data-testid="promptTextNext"
                    id="promptTextNext"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        dispatch(advanceStepper())
                    }}
                    disabled={nextDisabled}
                >
                    Next
                </Button>
            </Grid>
        </Grid>
    )
}
